
import { spfi, SPFI } from "@pnp/sp";
import { MSAL } from "@pnp/msaljsclient";
import { Logger, ConsoleListener } from "@pnp/logging";
import "@pnp/sp/webs";
import "@pnp/sp/lists";
import "@pnp/sp/items";
import { BrowserFetchWithRetry } from "@pnp/queryable";
import { msalInstance, tokenRequest } from "../msalAuth";
import * as msalAuth from "../msalAuth"; // Import everything from msalAuth

// Subscribe a logging observer with more verbose logging for debugging
Logger.subscribe(ConsoleListener());
// Replace the LogLevel.Verbose enum with a numeric value (3 is Verbose in PnP Logging)
Logger.activeLogLevel = 3; // Verbose level for more detailed logs

// THIS IS ONLY FOR SHAREPOINT CONNECTION
// Fixed MSAL options to use the proper audience format
const msalOptions = {
  auth: {
    clientId: "dc97a0f2-d73a-4184-8bfe-0c10ed7ff5df",
    authority: "https://login.microsoftonline.com/b24752cf-dca1-4cd9-8094-aeeb93913ea5",
    redirectUri: window.location.origin,
  },
  // Use the same scopes as in tokenRequest
  scopes: ["https://netorgft10863452.sharepoint.com/.default"],
  
  // Adding resource identifier to ensure proper audience validation
  resource: "00000003-0000-0ff1-ce00-000000000000/netorgft10863452.sharepoint.com@b24752cf-dca1-4cd9-8094-aeeb93913ea5",
  
  // Make sure we pass the correct domain hint
  extraQueryParameters: {
    domain_hint: "organizations"
  }
};

// Create a default SP instance to start with
let sp: SPFI = spfi();

// Function to initialize or reinitialize the SP instance with a specific site URL
export const initializeSP = (siteUrl: string): void => {
  console.log("[PnPjs] Initializing SP with site URL:", siteUrl);
  try {
    // Make sure PnPjs uses the right resource format for SharePoint
    const spInstance = spfi(siteUrl);
    
    // Apply the browser fetch with retry behavior
    spInstance.using(BrowserFetchWithRetry());
    
    // Apply MSAL configuration
    spInstance.using(MSAL(msalOptions));
    
    // Set this as our global SP instance
    sp = spInstance;
    
    console.log("[PnPjs] SP instance initialized with site URL:", siteUrl);
  } catch (error) {
    console.error("[PnPjs] Error initializing SP instance:", error);
  }
};

// Export a function that returns the SPFI instance
export async function getSP(): Promise<SPFI> {
  try {
    console.log("[PnPjs] Getting SP instance...");
    
    // Ensure an active account is set
    msalAuth.setActiveAccountIfNeeded();
    
    // Check if we have an active account
    const activeAccount = msalInstance.getActiveAccount();
    
    if (!activeAccount) {
      console.log("[PnPjs] No active account found, initiating login for SharePoint connection");
      const loginResponse = await msalInstance.loginPopup({
        ...tokenRequest,
        redirectUri: window.location.origin,
        extraQueryParameters: {
          domain_hint: "organizations",
          resource: "00000003-0000-0ff1-ce00-000000000000/netorgft10863452.sharepoint.com@b24752cf-dca1-4cd9-8094-aeeb93913ea5"
        }
      });
      msalInstance.setActiveAccount(loginResponse.account);
      console.log("[PnPjs] Login successful, account set");
    } else {
      // Try to acquire a token silently
      try {
        console.log("[PnPjs] Acquiring token silently with active account");
        await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account: activeAccount,
          extraQueryParameters: {
            domain_hint: "organizations",
            resource: "00000003-0000-0ff1-ce00-000000000000/netorgft10863452.sharepoint.com@b24752cf-dca1-4cd9-8094-aeeb93913ea5"
          }
        });
        console.log("[PnPjs] Silent token acquisition successful");
      } catch (error) {
        console.warn("[PnPjs] Silent token acquisition failed, fallback to interactive", error);
        const popupResponse = await msalInstance.acquireTokenPopup({
          ...tokenRequest,
          account: activeAccount,
          redirectUri: window.location.origin,
          extraQueryParameters: {
            domain_hint: "organizations",
            resource: "00000003-0000-0ff1-ce00-000000000000/netorgft10863452.sharepoint.com@b24752cf-dca1-4cd9-8094-aeeb93913ea5"
          }
        });
        
        // Update the active account from the response
        if (popupResponse.account) {
          msalInstance.setActiveAccount(popupResponse.account);
          console.log("[PnPjs] Updated active account after popup authentication");
        }
      }
    }
    
    console.log("[PnPjs] Successfully acquired token, returning SP instance");
    return sp;
  } catch (error) {
    console.error("[PnPjs] Error in getSP:", error);
    throw error;
  }
}

// Export the MSAL instance
export { msalInstance };
